import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { AlertTriangle, Download, ExternalLink, FileText, Loader2, RotateCcw } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import { Label } from '../components/ui/label';
import { Skeleton } from '../components/ui/skeleton';
import { Textarea } from '../components/ui/textarea';
import { useAuth } from '../hooks/useAuth';
import { useBreadcrumb } from '../hooks/useBreadcrumb';
import { cragsService } from '../services/crags';
import { zonesService } from '../services/zones';
import { climbsService } from '../services/climbs';
import { canPrintCragSheet } from '../components/crag-sheet/sheet-access';
import { loadSheetMaintainers } from '../components/crag-sheet/sheet-logos';
import { loadSheetTopos } from '../components/crag-sheet/sheet-photos';
import type { SheetMaintainer } from '../components/crag-sheet/crag-sheet-pdf';
import type { RenderedSheet } from '../components/crag-sheet/render-crag-sheet-pdf';
import {
  COLUMN_CHOICES,
  DEFAULT_SETTINGS,
  MANUAL_FONT_SIZES,
  PAGE_CHOICES,
  SHEET_LANGUAGES,
  UNSECTORED_ID,
  buildSheetContent,
  cragDescription,
  printableClimbs,
  printableUrl,
  sheetFileName,
  sheetUrl,
  type SheetFit,
  type SheetGrouping,
  type SheetSettings,
} from '../components/crag-sheet/sheet-data';
import { trackEvent } from '../utils/analytics';
import { cn } from '../lib/utils';
import type { Climb } from '../types/climb';
import type { Crag } from '../types/crag';
import type { Zone } from '../types/zone';

type LoadState = 'loading' | 'ready' | 'not_found';

type PdfState =
  | { status: 'idle' }
  | { status: 'rendering'; previous: RenderedPdf | null }
  | { status: 'ready'; pdf: RenderedPdf }
  | { status: 'error' };

interface RenderedPdf extends Omit<RenderedSheet, 'blob'> {
  url: string;
  /** A sector's photo could not be read, and the sheet came out without it. */
  topoFailed: boolean;
}

/** Typing in the description re-renders the sheet once the typing stops. */
const RENDER_DEBOUNCE_MS = 450;

const storageKey = (slug: string) => `crag-sheet:${slug}`;

/**
 * The settings last used for this crag, so a description shortened for print
 * is still shortened the next time the sheet is printed. Only what is known to
 * be the right shape is taken back: a stored value from an older version of
 * this page must not break it.
 */
function loadSettings(slug: string): SheetSettings {
  try {
    const raw = localStorage.getItem(storageKey(slug));
    if (!raw) return DEFAULT_SETTINGS;
    const stored = JSON.parse(raw) as Partial<SheetSettings>;
    return {
      language: SHEET_LANGUAGES.includes(stored.language as never) ? stored.language! : DEFAULT_SETTINGS.language,
      grouping: stored.grouping === 'name' ? 'name' : 'sector',
      fit: validFit(stored.fit) ?? DEFAULT_SETTINGS.fit,
      excludedSectorIds: Array.isArray(stored.excludedSectorIds) ? stored.excludedSectorIds.map(String) : [],
      showTopo: stored.showTopo !== false,
      descriptions: {
        it: typeof stored.descriptions?.it === 'string' ? stored.descriptions.it : null,
        en: typeof stored.descriptions?.en === 'string' ? stored.descriptions.en : null,
      },
    };
  } catch {
    return DEFAULT_SETTINGS;
  }
}

function validFit(fit: unknown): SheetFit | null {
  if (!fit || typeof fit !== 'object') return null;
  const value = fit as Record<string, unknown>;
  if (value.mode === 'auto' && PAGE_CHOICES.includes(value.targetPages as never)) {
    return { mode: 'auto', targetPages: value.targetPages as number };
  }
  if (
    value.mode === 'manual' &&
    Object.values(MANUAL_FONT_SIZES).includes(value.fontSize as never) &&
    COLUMN_CHOICES.includes(value.columns as never)
  ) {
    return { mode: 'manual', fontSize: value.fontSize as number, columns: value.columns as number };
  }
  return null;
}

/** A row of mutually exclusive buttons — the page's one kind of choice. */
function Segmented<T extends string | number>({
  value,
  options,
  onChange,
  testId,
}: {
  value: T;
  options: { value: T; label: ReactNode }[];
  onChange: (value: T) => void;
  testId: string;
}) {
  return (
    <div className="inline-flex flex-wrap gap-1 rounded-lg border p-1" role="radiogroup" data-testid={testId}>
      {options.map((option) => (
        <button
          key={String(option.value)}
          type="button"
          role="radio"
          aria-checked={option.value === value}
          onClick={() => onChange(option.value)}
          data-testid={`${testId}-${option.value}`}
          className={cn(
            'rounded-md px-3 py-1 text-sm font-medium transition-colors',
            option.value === value ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted'
          )}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

function Field({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
  return (
    <div className="space-y-1.5">
      <p className="text-sm font-medium">{label}</p>
      {children}
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
}

/**
 * `/crags/:slug/sheet` — the crag's printable sheet: its name, description and
 * routes on A4, to laminate and put up at the cliff. For the crag's confirmed
 * admins and editors (canPrintCragSheet).
 *
 * The settings sit beside the actual PDF, which is made here in the browser
 * (react-pdf, loaded on its own) and made again as they change — what is
 * previewed is the file that prints, not an imitation of it.
 */
export default function CragSheetPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const allowed = !!slug && canPrintCragSheet(user, slug);

  const [load, setLoad] = useState<LoadState>('loading');
  const [crag, setCrag] = useState<Crag | null>(null);
  const [sectors, setSectors] = useState<Zone[]>([]);
  const [climbs, setClimbs] = useState<Climb[]>([]);
  const [maintainers, setMaintainers] = useState<SheetMaintainer[] | null>(null);
  const [settings, setSettings] = useState<SheetSettings>(() => (slug ? loadSettings(slug) : DEFAULT_SETTINGS));
  const [pdf, setPdf] = useState<PdfState>({ status: 'idle' });
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!slug || !allowed) return;
    let active = true;
    (async () => {
      try {
        const found = await cragsService.getCragBySlug(slug);
        const [zones, lines] = await Promise.all([
          zonesService.getZonesForLocation(slug, 'crag').catch(() => [] as Zone[]),
          climbsService.searchClimbs({ location_id: found.id }, { cache: 'no-cache' }),
        ]);
        if (!active) return;
        setCrag(found);
        setSectors(zones);
        setClimbs(lines);
        setLoad('ready');
        const logos = await loadSheetMaintainers(found.maintainers ?? []);
        if (active) setMaintainers(logos);
      } catch {
        if (active) setLoad('not_found');
      }
    })();
    return () => {
      active = false;
    };
  }, [slug, allowed]);

  useEffect(() => {
    if (!slug) return;
    try {
      localStorage.setItem(storageKey(slug), JSON.stringify(settings));
    } catch {
      // A full or blocked storage only costs remembering the settings.
    }
  }, [slug, settings]);

  const update = useCallback((patch: Partial<SheetSettings>) => {
    setSettings((current) => ({ ...current, ...patch }));
  }, []);

  const content = useMemo(
    () => (crag ? buildSheetContent(crag, sectors, climbs, settings) : null),
    [crag, sectors, climbs, settings]
  );

  // How many printable lines each sector checkbox stands for.
  const sectorCounts = useMemo(() => {
    const counts = new Map<string, number>();
    const known = new Set(sectors.map((sector) => sector.id));
    for (const climb of printableClimbs(climbs)) {
      const id = climb.zone?.id && known.has(climb.zone.id) ? climb.zone.id : UNSECTORED_ID;
      counts.set(id, (counts.get(id) ?? 0) + 1);
    }
    return counts;
  }, [sectors, climbs]);

  const sortedSectors = useMemo(
    () => [...sectors].sort((a, b) => (a.position ?? 0) - (b.position ?? 0)),
    [sectors]
  );

  // The sheet speaks its own language whatever the app is set to.
  const sheetT: TFunction =
    typeof i18n.getFixedT === 'function' ? i18n.getFixedT(settings.language) : t;

  const origin = window.location.origin;

  // Made again, a moment after the settings stop changing. The file on show
  // stays up while the next one is made, and its object URL is released once
  // it has been replaced.
  const shownUrl = useRef<string | null>(null);
  useEffect(() => {
    if (!content || !slug || maintainers === null) return;
    let active = true;
    setPdf((current) => ({
      status: 'rendering',
      previous: current.status === 'ready' ? current.pdf : current.status === 'rendering' ? current.previous : null,
    }));

    const timer = window.setTimeout(async () => {
      try {
        const { renderCragSheetPdf } = await import('../components/crag-sheet/render-crag-sheet-pdf');
        const { topos, failed: topoFailed } = await loadSheetTopos(content.topos);
        const rendered = await renderCragSheetPdf(
          { content, url: sheetUrl(origin, slug), printableUrl: printableUrl(origin, slug), maintainers, topos },
          settings.fit,
          sheetT
        );
        if (!active) return;
        const url = URL.createObjectURL(rendered.blob);
        if (shownUrl.current) URL.revokeObjectURL(shownUrl.current);
        shownUrl.current = url;
        setPdf({ status: 'ready', pdf: { url, layout: rendered.layout, headerCut: rendered.headerCut, topoFailed } });
      } catch (error) {
        console.error('Failed to build the crag sheet:', error);
        if (active) setPdf({ status: 'error' });
      }
    }, RENDER_DEBOUNCE_MS);

    return () => {
      active = false;
      window.clearTimeout(timer);
    };
    // `sheetT` is a new function on every render; the language, inside
    // `content`'s settings, is what it depends on.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [content, settings.fit, maintainers, slug, origin, attempt]);

  useEffect(
    () => () => {
      if (shownUrl.current) URL.revokeObjectURL(shownUrl.current);
    },
    []
  );

  useBreadcrumb(
    () => ({
      onBack: () => navigate(-1),
      segments: crag
        ? [
            { label: crag.name, onClick: () => navigate(`/crags/${crag.slug}`) },
            { label: t('cragSheet.page.breadcrumb') },
          ]
        : [],
      loading: allowed && load === 'loading',
    }),
    [crag?.name, crag?.slug, load, allowed]
  );

  if (!slug) return <Navigate to="/not-found" replace />;

  if (!allowed || load === 'not_found') {
    return (
      <div className="mx-auto max-w-3xl">
        <Card>
          <CardContent className="pt-6 text-center" data-testid={allowed ? 'crag-sheet-not-found' : 'crag-sheet-forbidden'}>
            <p className="text-sm text-muted-foreground">
              {allowed ? t('cragSheet.page.notFound') : t('cragSheet.page.forbidden')}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (load === 'loading' || !crag || !content) {
    return (
      <div className="mx-auto grid max-w-6xl gap-4 lg:grid-cols-[minmax(0,380px)_1fr]">
        <Skeleton className="h-[480px]" />
        <Skeleton className="aspect-[1/1.414]" />
      </div>
    );
  }

  const shown = pdf.status === 'ready' ? pdf.pdf : pdf.status === 'rendering' ? pdf.previous : null;
  const description = settings.descriptions[settings.language] ?? cragDescription(crag, settings.language);
  const hasUnsectored = (sectorCounts.get(UNSECTORED_ID) ?? 0) > 0;
  // Chrome on Android, among others, cannot show a PDF inside a page: there
  // the preview would be an empty box, so the file is offered to open instead.
  const canPreviewInline = typeof navigator === 'undefined' || navigator.pdfViewerEnabled !== false;

  const toggleSector = (id: string, included: boolean) => {
    const excluded = new Set(settings.excludedSectorIds);
    if (included) excluded.delete(id);
    else excluded.add(id);
    update({ excludedSectorIds: [...excluded] });
  };

  const sectorRow = (id: string, name: string) => (
    <li key={id} className="flex items-center gap-2">
      <Checkbox
        id={`sheet-sector-${id}`}
        checked={!settings.excludedSectorIds.includes(id)}
        onCheckedChange={(checked) => toggleSector(id, checked === true)}
        data-testid={`crag-sheet-sector-${id}`}
      />
      <Label htmlFor={`sheet-sector-${id}`} className="flex-1 font-normal">
        {name}
      </Label>
      <span className="text-xs text-muted-foreground">
        {t('cragSheet.page.routesInSector', { count: sectorCounts.get(id) ?? 0 })}
      </span>
    </li>
  );

  // Offered only where it would put something on the sheet.
  const hasPhotos = sortedSectors.some(
    (sector) =>
      !!sector.image_url && !settings.excludedSectorIds.includes(sector.id) && (sectorCounts.get(sector.id) ?? 0) > 0
  );
  const noLinesDrawn = content.topos.length > 0 && content.topos.every((topo) => topo.lines.length === 0);

  const fit = settings.fit;
  const manualFit = fit.mode === 'manual' ? fit : { mode: 'manual' as const, fontSize: MANUAL_FONT_SIZES.m, columns: 3 };

  return (
    <div className="mx-auto grid max-w-6xl gap-4 lg:grid-cols-[minmax(0,380px)_1fr]" data-testid="crag-sheet">
      <Card className="h-fit">
        <CardContent className="space-y-5 p-4 sm:p-5">
          <div className="flex items-start gap-3">
            <FileText className="mt-0.5 size-6 shrink-0 text-primary" aria-hidden />
            <div className="min-w-0">
              <h1 className="text-lg font-semibold">{t('cragSheet.page.title')}</h1>
              <p className="text-sm text-muted-foreground">{t('cragSheet.page.description')}</p>
            </div>
          </div>

          <Field label={t('cragSheet.page.language')}>
            <Segmented
              testId="crag-sheet-language"
              value={settings.language}
              options={SHEET_LANGUAGES.map((code) => ({ value: code, label: code.toUpperCase() }))}
              onChange={(language) => update({ language })}
            />
          </Field>

          <Field label={t('cragSheet.page.grouping')}>
            <Segmented<SheetGrouping>
              testId="crag-sheet-grouping"
              value={settings.grouping}
              options={[
                { value: 'sector', label: t('cragSheet.page.groupingSector') },
                { value: 'name', label: t('cragSheet.page.groupingName') },
              ]}
              onChange={(grouping) => update({ grouping })}
            />
          </Field>

          {(sortedSectors.length > 0 || hasUnsectored) && (
            <Field label={t('cragSheet.page.sectors')}>
              <ul className="space-y-2">
                {sortedSectors.map((sector) => sectorRow(sector.id, sector.name))}
                {hasUnsectored && sectorRow(UNSECTORED_ID, t('cragSheet.page.otherRoutes'))}
              </ul>
            </Field>
          )}

          {hasPhotos && (
            <div className="space-y-1.5">
              <div className="flex items-center gap-2">
                <Checkbox
                  id="crag-sheet-topo"
                  checked={settings.showTopo}
                  onCheckedChange={(checked) => update({ showTopo: checked === true })}
                  data-testid="crag-sheet-topo"
                />
                <Label htmlFor="crag-sheet-topo" className="text-sm font-medium">
                  {t('cragSheet.page.topo')}
                </Label>
              </div>
              <p className="text-xs text-muted-foreground" data-testid="crag-sheet-topo-hint">
                {noLinesDrawn ? t('cragSheet.page.topoNoLines') : t('cragSheet.page.topoHint')}
              </p>
            </div>
          )}

          <Field label={t('cragSheet.page.fit')} hint={fit.mode === 'auto' ? t('cragSheet.page.targetPagesHint') : undefined}>
            <Segmented
              testId="crag-sheet-fit"
              value={fit.mode}
              options={[
                { value: 'auto', label: t('cragSheet.page.fitAuto') },
                { value: 'manual', label: t('cragSheet.page.fitManual') },
              ]}
              onChange={(mode) =>
                update({ fit: mode === 'auto' ? { mode: 'auto', targetPages: 1 } : manualFit })
              }
            />
            {fit.mode === 'auto' ? (
              <div className="flex flex-wrap items-center gap-2 pt-1">
                <span className="text-sm text-muted-foreground">{t('cragSheet.page.targetPages')}</span>
                <Segmented
                  testId="crag-sheet-pages"
                  value={fit.targetPages}
                  options={PAGE_CHOICES.map((count) => ({ value: count, label: count }))}
                  onChange={(targetPages) => update({ fit: { mode: 'auto', targetPages } })}
                />
              </div>
            ) : (
              <div className="space-y-2 pt-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="w-20 text-sm text-muted-foreground">{t('cragSheet.page.fontSize')}</span>
                  <Segmented
                    testId="crag-sheet-size"
                    value={fit.fontSize}
                    options={Object.entries(MANUAL_FONT_SIZES).map(([key, size]) => ({
                      value: size,
                      label: t(`cragSheet.page.size_${key}`),
                    }))}
                    onChange={(fontSize) => update({ fit: { ...fit, fontSize } })}
                  />
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="w-20 text-sm text-muted-foreground">{t('cragSheet.page.columns')}</span>
                  <Segmented
                    testId="crag-sheet-columns"
                    value={fit.columns}
                    options={COLUMN_CHOICES.map((count) => ({ value: count, label: count }))}
                    onChange={(columns) => update({ fit: { ...fit, columns } })}
                  />
                </div>
              </div>
            )}
          </Field>

          <div className="space-y-1.5">
            <div className="flex items-center justify-between gap-2">
              <Label htmlFor="crag-sheet-description" className="text-sm font-medium">
                {t('cragSheet.page.descriptionLabel')}
              </Label>
              {settings.descriptions[settings.language] !== null && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 gap-1.5 px-2 text-xs"
                  onClick={() => update({ descriptions: { ...settings.descriptions, [settings.language]: null } })}
                  data-testid="crag-sheet-description-reset"
                >
                  <RotateCcw className="size-3.5" />
                  {t('cragSheet.page.resetDescription')}
                </Button>
              )}
            </div>
            <Textarea
              id="crag-sheet-description"
              value={description}
              rows={7}
              onChange={(event) =>
                update({ descriptions: { ...settings.descriptions, [settings.language]: event.target.value } })
              }
              data-testid="crag-sheet-description"
            />
            <p className="text-xs text-muted-foreground">{t('cragSheet.page.descriptionHint')}</p>
          </div>
        </CardContent>
      </Card>

      <Card className="h-fit">
        <CardContent className="space-y-3 p-4 sm:p-5">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="mr-auto font-semibold">{t('cragSheet.page.preview')}</h2>
            {pdf.status === 'rendering' && (
              <Loader2 className="size-4 animate-spin text-muted-foreground" aria-label={t('cragSheet.page.preparing')} />
            )}
            {shown && (
              <>
                <Button asChild variant="outline" size="sm" className="gap-2">
                  <a href={shown.url} target="_blank" rel="noopener noreferrer" data-testid="crag-sheet-open">
                    <ExternalLink className="size-4" />
                    {t('cragSheet.page.open')}
                  </a>
                </Button>
                <Button asChild size="sm" className="gap-2">
                  <a
                    href={shown.url}
                    download={sheetFileName(slug, settings.language)}
                    onClick={() => trackEvent('crag_sheet_downloaded', { crag_slug: slug })}
                    data-testid="crag-sheet-download"
                  >
                    <Download className="size-4" />
                    {t('cragSheet.page.download')}
                  </a>
                </Button>
              </>
            )}
          </div>

          {shown && (
            <div className="space-y-1.5 text-sm">
              <p className="text-muted-foreground" data-testid="crag-sheet-summary">
                {t('cragSheet.page.summary', {
                  count: shown.layout.pages.length,
                  size: shown.layout.fontSize,
                  columns: shown.layout.columns,
                })}
              </p>
              {content.stats.routes === 0 && <Warning>{t('cragSheet.page.noRoutes')}</Warning>}
              {!shown.layout.fits && fit.mode === 'auto' && (
                <Warning>
                  {t('cragSheet.page.doesNotFit', {
                    target: t('cragSheet.page.pagesTarget', { count: fit.targetPages }),
                    count: shown.layout.pages.length,
                  })}
                </Warning>
              )}
              {shown.headerCut && <Warning>{t('cragSheet.page.headerCut')}</Warning>}
              {shown.topoFailed && <Warning>{t('cragSheet.page.topoFailed')}</Warning>}
            </div>
          )}

          {pdf.status === 'error' && (
            <div className="flex flex-wrap items-center gap-3">
              <p className="text-sm text-destructive" data-testid="crag-sheet-error">
                {t('cragSheet.page.error')}
              </p>
              <Button variant="outline" size="sm" className="gap-2" onClick={() => setAttempt((count) => count + 1)}>
                <RotateCcw className="size-4" />
                {t('cragSheet.page.retry')}
              </Button>
            </div>
          )}

          {!shown && pdf.status !== 'error' && (
            <div className="flex aspect-[1/1.414] items-center justify-center rounded-md border bg-muted/40">
              <p className="flex items-center gap-2 text-sm text-muted-foreground" data-testid="crag-sheet-preparing">
                <Loader2 className="size-4 animate-spin" />
                {t('cragSheet.page.preparing')}
              </p>
            </div>
          )}

          {shown && canPreviewInline && (
            <iframe
              title={t('cragSheet.page.preview')}
              src={`${shown.url}#view=FitH&toolbar=0`}
              className={cn(
                'aspect-[1/1.414] w-full rounded-md border bg-white transition-opacity',
                pdf.status === 'rendering' && 'opacity-60'
              )}
              data-testid="crag-sheet-preview"
            />
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function Warning({ children }: { children: ReactNode }) {
  return (
    <p className="flex items-start gap-1.5 text-amber-700 dark:text-amber-400">
      <AlertTriangle className="mt-0.5 size-4 shrink-0" />
      <span>{children}</span>
    </p>
  );
}
